import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import type { BlockAvailabilityRecord } from '@/types/block-availability-api';

/**
 * The GET both block-hydration hooks run — this client's `GetBlockAppointmentCore`.
 *
 * The route is the only thing `useBlockAppointment` and
 * `useBlockResourceAppointment` disagree about (PROD-2512, Scheduling
 * `5acfb488`), exactly as it is the only thing `useSaveBlockSlot` and
 * `useSaveBlockResourceSlot` disagree about, so the request is written once
 * here and imported rather than restated. Two copies of an id-encoding or
 * signal-plumbing detail is the drift mirroring exists to avoid.
 *
 * `R` is the record shape the caller expects back: the provider route answers
 * `BlockAvailabilityRecord`, the device route the same record plus its
 * `participant` array.
 */
export function getBlockAppointmentFrom<R>(
  route: string,
  blockId: string,
  signal: AbortSignal,
): Promise<R> {
  return get<R>(`${route}?id=${encodeURIComponent(blockId)}`, { signal });
}

/**
 * GET /api/Appointment/BlockAppointment?id={id} — the PROVIDER block-hydration
 * route.
 *
 * Loads an existing block-availability record for the edit panel.
 * Flutter parity: `scheduling_data_source_impl.dart:455-473` and
 * `block_availability_widget.dart:82-86` (`FetchBlockAvailabilityEvent`).
 *
 * Response shape is `BlockAvailabilityRecord` (`block_availability_model.dart:54-79`).
 * GET takes URL params only — no body, no wire-shape risk. Response
 * type AUTHORITATIVE from Dart source per CLAUDE.md conversion
 * contract; mutation hooks (Unblock) still gated on dev curl.
 *
 * PROD-2512: this route now REFUSES a device-owned block with a 400 ("This
 * block belongs to a device; open it through the device route.") instead of the
 * 500 it used to answer. A device block belongs on
 * `useBlockResourceAppointment`; `BlockAvailabilityPanel` picks between the two
 * from `state.actorKind`, the same discriminant that already picks the save and
 * unblock routes.
 */
export function useBlockAppointment(blockId: string | null) {
  return useQuery({
    queryKey: ['block-appointment', blockId],
    queryFn: ({ signal }) => {
      if (!blockId) throw new Error('useBlockAppointment: blockId is required');
      return getBlockAppointmentFrom<BlockAvailabilityRecord>(
        '/api/Appointment/BlockAppointment',
        blockId,
        signal,
      );
    },
    enabled: !!blockId,
  });
}
